import { useState } from "react";
import { Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import heroImg from "@/assets/hero-landing.jpg";
import logoImg from "@/assets/logo.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      toast({ title: "Login failed", description: error.message, variant: "destructive" });
      setLoading(false);
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      const roles = roleData?.map(r => r.role) || [];
      if (roles.includes("admin")) navigate("/admin", { replace: true });
      else if (roles.includes("growlearn")) navigate("/growlearn", { replace: true });
      else if (roles.includes("aca_member")) navigate("/aca", { replace: true });
      else navigate("/student", { replace: true });
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Full-screen background */}
      <img
        src={heroImg}
        alt="Professional background"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 hero-overlay" />

      {/* Back to home */}
      <a
        href="https://acaglobal.my/"
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Home
      </a>

      {/* Glass card */}
      <div className="relative z-10 w-full max-w-md mx-4 animate-fade-in">
        <div className="glass-login rounded-2xl p-8 md:p-10 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-white rounded-xl p-2 shadow-lg">
                <img src={logoImg} alt="ACA" className="h-12 w-12 object-contain rounded-lg" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white">ACA Membership</h1>
            <p className="mt-2 text-white/50 text-sm">Sign in to access your portal</p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80 text-sm">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="input-glass h-11 rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80 text-sm">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="input-glass h-11 rounded-lg pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 rounded-lg gradient-warm border-0 text-white font-semibold hover:opacity-90 transition-all duration-200"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in...
                </span>
              ) : "SIGN IN"}
            </Button>

            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-white/50 hover:text-white transition-colors">
                Forgot Password?
              </Link>
            </div>
          </form>

          <p className="mt-6 text-center text-xs text-white/35">
            Contact your GrowLearn administrator for account access.
          </p>
        </div>

        {/* Stats strip */}
        <div className="flex justify-center gap-8 mt-8 text-center">
          {[
            { value: "500+", label: "Members" },
            { value: "100%", label: "Digital" },
            { value: "24h", label: "Approval" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-lg font-bold text-white/80">{s.value}</p>
              <p className="text-xs text-white/40">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Login;
